import React, { useMemo, useState, useId, useEffect } from "react";
import styles from "./YoutubeEmbed.module.css";

/**
 * Extract a YouTube video ID from any supported URL shape.
 */
export const getYoutubeVideoId = (url) => {
  if (!url || typeof url !== "string") return null;

  let parsed;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }

  if (parsed.protocol !== "https:") return null;

  const allowedHosts = [
    "youtube.com",
    "www.youtube.com",
    "m.youtube.com",
    "youtu.be",
    "youtube-nocookie.com",
    "www.youtube-nocookie.com",
  ];
  if (!allowedHosts.includes(parsed.hostname)) return null;

  const idPattern = /^[a-zA-Z0-9_-]{11}$/;

  if (parsed.hostname === "youtu.be") {
    const id = parsed.pathname.replace("/", "");
    return idPattern.test(id) ? id : null;
  }

  if (parsed.pathname === "/watch") {
    const id = parsed.searchParams.get("v");
    return id && idPattern.test(id) ? id : null;
  }

  const match = parsed.pathname.match(
    /^\/(embed|shorts|v)\/([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[2] : null;
};

/**
 * Content block renderer (unchanged).
 */
const ContentBlock = ({ block }) => {
  if (!block || typeof block !== "object") return null;

  const renderChildren = () =>
    Array.isArray(block.children) && block.children.length > 0 ? (
      <div className={styles.nested}>
        {block.children.map((child, idx) => (
          <ContentBlock key={idx} block={child} />
        ))}
      </div>
    ) : null;

  switch (block.type) {
    case "heading":
      return (
        <>
          <h3 className={styles.blockHeading}>{block.text}</h3>
          {renderChildren()}
        </>
      );

    case "subheading":
      return (
        <>
          <h4 className={styles.blockSubheading}>{block.text}</h4>
          {renderChildren()}
        </>
      );

    case "paragraph":
      return (
        <>
          <p className={styles.blockParagraph}>{block.text}</p>
          {renderChildren()}
        </>
      );

    case "list":
      return (
        <>
          <ul className={styles.blockList}>
            {block.items?.map((item, idx) => {
              if (typeof item === "string") return <li key={idx}>{item}</li>;

              if (item && typeof item === "object") {
                return (
                  <li key={idx}>
                    {item.label && (
                      <strong className={styles.blockLabel}>
                        {item.label}
                      </strong>
                    )}
                    {item.label && item.text ? " " : null}
                    {item.text}
                    {Array.isArray(item.children) &&
                      item.children.length > 0 && (
                        <div className={styles.nested}>
                          {item.children.map((child, cIdx) => (
                            <ContentBlock key={cIdx} block={child} />
                          ))}
                        </div>
                      )}
                  </li>
                );
              }
              return null;
            })}
          </ul>
          {renderChildren()}
        </>
      );

    case "labeledList":
      return (
        <>
          <ul className={styles.blockList}>
            {block.items?.map((item, idx) => (
              <li key={idx}>
                {item.label && (
                  <strong className={styles.blockLabel}>{item.label}</strong>
                )}{" "}
                {item.text}
                {Array.isArray(item.children) && item.children.length > 0 && (
                  <div className={styles.nested}>
                    {item.children.map((child, cIdx) => (
                      <ContentBlock key={cIdx} block={child} />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {renderChildren()}
        </>
      );

    case "group":
      return (
        <div className={styles.group}>
          {Array.isArray(block.items) &&
            block.items.map((child, idx) => (
              <ContentBlock key={idx} block={child} />
            ))}
          {renderChildren()}
        </div>
      );
    case "liveDemo":
      return (
        <>
          <div className={styles.liveDemo}>
            <div className={styles.liveDemoAccent} />
            <div className={styles.liveDemoBody}>
              <h4 className={styles.liveDemoTitle}>
                {block.title || "Want to Explore the Live Demo?"}
              </h4>
              <p className={styles.liveDemoText}>
                {block.text ||
                  "We've set up a read-only demo site with all the transactions shown in the video. Browse the system at your own pace - Sales Orders, Work Orders, Molds, NOK Rejection Logs, everything."}
              </p>

              {block.url && (
                <div className={styles.liveDemoRow}>
                  <span aria-hidden="true">🔗</span>
                  <span className={styles.liveDemoLabel}>Demo:</span>
                  <a
                    href={block.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.liveDemoLink}
                  >
                    {block.urlLabel ||
                      block.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                </div>
              )}

              {block.email && (
                <div className={styles.liveDemoRow}>
                  <span aria-hidden="true">📧</span>
                  <span className={styles.liveDemoLabel}>
                    Contact us for login credentials:
                  </span>
                  <a
                    href={`mailto:${block.email}`}
                    className={styles.liveDemoLink}
                  >
                    {block.email}
                  </a>
                </div>
              )}
            </div>
          </div>
          {renderChildren()}
        </>
      );
    default:
      return null;
  }
};

/**
 * Accordion-style YouTube embed.
 * - `defaultOpen` controls whether this card starts expanded.
 * - Parent should pass `defaultOpen={index === 0}` when mapping a list,
 *   so only the first card is open by default.
 */
const YoutubeEmbed = ({
  title,
  subtitle,
  url,
  content,
  className = "",
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // 🔄 Sync open state when parent changes defaultOpen (e.g. on hash change)
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);
  const panelId = useId();
  const videoId = useMemo(() => getYoutubeVideoId(url), [url]);

  return (
    <div className={`cardShadow mt-4 ${styles.wrapper} ${className}`}>
      {/* Accordion header - title + subtitle, clickable */}
      <button
        type="button"
        className={styles.header}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <div className={styles.headerText}>
          {title && <h2 className={styles.title}>{title}</h2>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <span
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
          aria-hidden="true"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {/* Collapsible panel */}
      <div
        id={panelId}
        role="region"
        className={`${styles.panel} ${open ? styles.panelOpen : ""}`}
      >
        <div className={styles.playerContainer}>
          {videoId ? (
            <iframe
              width="560"
              height="315"
              className={styles.player}
              src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
              title={title || "YouTube video player"}
              loading="lazy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <div className={styles.placeholder}>
              <span>Invalid or unsupported YouTube URL</span>
            </div>
          )}
        </div>

        {Array.isArray(content) && content.length > 0 && (
          <div className={styles.content}>
            {content.map((block, idx) => (
              <ContentBlock key={idx} block={block} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default YoutubeEmbed;
